import { Modal, Form, Select, Input, DatePicker, Button, Popconfirm, Tabs, Alert } from 'antd'
import { useState, useEffect } from 'react'
import { DeleteOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons'
import dayjs, { Dayjs } from 'dayjs'
import { ConsultationCalendarEvent } from '../scheduleApi'
import { useUpdateConsultationMutation, useDeleteConsultationMutation, useConsultationProjectsQuery } from './consultationQueries'
import { ConsultationStatuses, consultationStatusName, ConsultationStatus, UpdateConsultationRequest } from './consultationApi'
import { useSalesPeopleQuery } from '../../RouteManagement/routingQueries'
import { useSalesLeads } from '../../SystemSetup/SalesLeads/salesLeadsQueries'

const { TextArea } = Input

type Props = {
  open: boolean
  onClose: () => void
  consultation: ConsultationCalendarEvent | null
  onSuccess?: () => void
}

const EditConsultationModal = ({ open, onClose, consultation, onSuccess }: Props) => {
  const [form] = Form.useForm()
  const [organizerEmail, setOrganizerEmail] = useState<string>('')
  const [projectId, setProjectId] = useState<number | null>(null)
  const [timeFrame, setTimeFrame] = useState<string>('')
  const [bestTimeToCall, setBestTimeToCall] = useState<string>('')
  const [consultationDateTime, setConsultationDateTime] = useState<Dayjs>(dayjs().hour(8).minute(0))
  const [fee, setFee] = useState<string>('')
  const [selectedSalesLeads, setSelectedSalesLeads] = useState<string[]>([])
  const [emailTemplateGuid, setEmailTemplateGuid] = useState<string | null>(null)
  const [summary, setSummary] = useState<string>('')
  const [status, setStatus] = useState<ConsultationStatus>(ConsultationStatuses.Scheduled)
  const [description, setDescription] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [emailBody, setEmailBody] = useState<string>('')

  const calendarEventId = consultation?.id ?? null
  const customerId = consultation?.customer?.id ?? null

  const { data: salesPeople } = useSalesPeopleQuery()
  const { data: salesLeads } = useSalesLeads()
  const { data: projectsData } = useConsultationProjectsQuery(customerId)

  const updateConsultationMutation = useUpdateConsultationMutation()
  const deleteConsultationMutation = useDeleteConsultationMutation()

  const salesLeadOptions = (() => {
    const optionsFromQuery = salesLeads?.map(lead => ({ value: lead.name, label: lead.name })) ?? []
    const existingNames = new Set(optionsFromQuery.map(o => o.value))
    const additionalOptions = selectedSalesLeads
      .filter(name => !existingNames.has(name))
      .map(name => ({ value: name, label: name }))
    return [...optionsFromQuery, ...additionalOptions]
  })()

  useEffect(() => {
    if (open && consultation) {
      const c = consultation.consultation
      setOrganizerEmail(consultation.organizerEmail || '')
      setProjectId(c.jobId)
      setTimeFrame(c.timeFrame || '')
      setBestTimeToCall(c.bestTimeToCall || '')

      const consultDateTime = dayjs(consultation.start)
      setConsultationDateTime(consultDateTime)

      setFee(c.fee || '')
      setSelectedSalesLeads(c.salesLeads || [])
      setEmailTemplateGuid(c.emailTemplateId?.toString() || null)
      setSummary(c.summary || '')
      setStatus(consultation.consultationStatus as ConsultationStatus)
      setDescription(c.description || '')
      setNotes(c.notes || '')
      setEmailBody(c.emailBody || '')

      form.setFieldsValue({
        organizerEmail: consultation.organizerEmail,
        projectId: c.jobId,
        timeFrame: c.timeFrame,
        bestTimeToCall: c.bestTimeToCall,
        consultationDateTime: consultDateTime,
        fee: c.fee,
        salesLeads: c.salesLeads || [],
        emailTemplateGuid: c.emailTemplateId?.toString(),
        summary: c.summary,
        status: consultation.consultationStatus,
        description: c.description,
        notes: c.notes
      })
    }
  }, [open, consultation, form])

  const handleClose = () => {
    form.resetFields()
    setOrganizerEmail('')
    setProjectId(null)
    setTimeFrame('')
    setBestTimeToCall('')
    setConsultationDateTime(dayjs().hour(8).minute(0))
    setFee('')
    setSelectedSalesLeads([])
    setEmailTemplateGuid(null)
    setSummary('')
    setStatus(ConsultationStatuses.Scheduled)
    setDescription('')
    setNotes('')
    setEmailBody('')
    onClose()
  }

  const handleSave = async () => {
    if (!consultation) {
      return
    }

    const consultDateTime = consultationDateTime.second(0)

    const updatedConsultation: UpdateConsultationRequest = {
      id: consultation.consultationId,
      customerId: consultation.customer.id,
      calendarEventId: consultation.id,
      organizerEmail,
      projectId,
      timeFrame: timeFrame || null,
      bestTimeToCall: bestTimeToCall || null,
      consultationDate: consultDateTime.format('MM/DD/YYYY h:mm A'),
      fee: fee || null,
      salesLead: selectedSalesLeads.length > 0 ? selectedSalesLeads.join(',') : '',
      emailTemplateGuid,
      summary,
      status,
      description: description || null,
      notes: notes || null,
      emailBody: emailBody || null
    }

    await updateConsultationMutation.mutateAsync(updatedConsultation)
    onSuccess?.()
    handleClose()
  }

  const handleDelete = async () => {
    if (!calendarEventId) {
      return
    }

    await deleteConsultationMutation.mutateAsync(calendarEventId)
    onSuccess?.()
    handleClose()
  }

  const customerAddress = consultation?.customer ? [
    consultation.customer.address1,
    consultation.customer.city,
    consultation.customer.state,
    consultation.customer.zip
  ].filter(Boolean).join(', ') : ''

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      onOk={handleSave}
      okText="Save"
      okButtonProps={{ loading: updateConsultationMutation.isLoading }}
      cancelText="Cancel"
      width={800}
      title={
        <div>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
            Edit Consultation
          </div>
          <div style={{ fontSize: '12px', fontWeight: 400, color: '#666', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>
              {consultation?.customer.displayName}
              {customerAddress && <> | {customerAddress}</>}
            </span>
            {consultation?.customer.id && (
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                style={{ color: '#1677ff', cursor: 'pointer', fontSize: '11px' }}
                onClick={() => window.open(`/#!/customers#${consultation.customer.id}`, '_blank')}
              />
            )}
          </div>
        </div>
      }
      footer={(_, { OkBtn, CancelBtn }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px' }}>
          <Popconfirm
            title="Remove from Schedule"
            description="Are you sure you want to remove this consultation from the schedule?"
            onConfirm={handleDelete}
            okText="Yes, Remove"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={deleteConsultationMutation.isLoading}
            >
              Remove from Schedule
            </Button>
          </Popconfirm>
          <div style={{ display: 'flex', gap: '8px' }}>
            <CancelBtn />
            <OkBtn />
          </div>
        </div>
      )}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: '24px' }}
      >
        <Tabs
          defaultActiveKey="sales"
          items={[
            {
              key: 'sales',
              label: 'Sales Information',
              children: (
                <>
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Sales Person</div>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select sales person"
                        value={organizerEmail || undefined}
                        onChange={setOrganizerEmail}
                        allowClear
                      >
                        {salesPeople?.map((person) => (
                          <Select.Option key={person.email} value={person.email}>
                            {person.displayName}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>

                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Associated Project</div>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select project"
                        value={projectId || undefined}
                        onChange={setProjectId}
                        allowClear
                      >
                        {projectsData?.unscheduled && projectsData.unscheduled.length > 0 && (
                          <Select.OptGroup label="Unscheduled">
                            {projectsData.unscheduled.map((project) => (
                              <Select.Option key={project.id} value={project.id}>
                                {project.name}{project.no ? ` (${project.no})` : ''}
                              </Select.Option>
                            ))}
                          </Select.OptGroup>
                        )}
                        {projectsData?.scheduled && projectsData.scheduled.length > 0 && (
                          <Select.OptGroup label="Scheduled">
                            {projectsData.scheduled.map((project) => (
                              <Select.Option key={project.id} value={project.id}>
                                {project.name}{project.no ? ` (${project.no})` : ''}
                              </Select.Option>
                            ))}
                          </Select.OptGroup>
                        )}
                      </Select>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Time Frame</div>
                      <Input
                        value={timeFrame}
                        onChange={(e) => setTimeFrame(e.target.value)}
                        placeholder="e.g. 4-6 Weeks"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Best Time to Call</div>
                      <Input
                        value={bestTimeToCall}
                        onChange={(e) => setBestTimeToCall(e.target.value)}
                        placeholder="e.g., After 5pm"
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Consultation Date</div>
                      <DatePicker
                        value={consultationDateTime}
                        onChange={(date) => date && setConsultationDateTime(date)}
                        style={{ width: '100%' }}
                        format="MMM D, YYYY h:mm A"
                        showTime={{ format: 'h:mm A', minuteStep: 5 }}
                        allowClear={false}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Consultation Fee</div>
                      <Input
                        value={fee}
                        onChange={(e) => setFee(e.target.value)}
                        placeholder="Enter fee amount"
                      />
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Sales Leads</div>
                    <Select
                      mode="multiple"
                      style={{ width: '100%' }}
                      placeholder="Select sales leads"
                      value={selectedSalesLeads}
                      onChange={setSelectedSalesLeads}
                      options={salesLeadOptions}
                    />
                  </div>
                </>
              )
            },
            {
              key: 'details',
              label: 'Details',
              children: (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Status</div>
                    <Select
                      style={{ width: '100%' }}
                      value={status}
                      onChange={setStatus}
                    >
                      {Object.entries(ConsultationStatuses).map(([, value]) => (
                        <Select.Option key={value} value={value}>
                          {consultationStatusName(value)}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Event Description</div>
                    <TextArea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter event description"
                      rows={3}
                    />
                  </div>

                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Internal Notes</div>
                    <TextArea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Enter internal notes"
                      rows={3}
                    />
                  </div>
                </>
              )
            },
            {
              key: 'email',
              label: 'Email',
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    message="This consultation's email has already been sent and cannot be edited."
                    style={{ marginBottom: '16px' }}
                  />

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Email Subject</div>
                    <Input value={summary} disabled />
                  </div>

                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Email Body</div>
                    <div
                      style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px 12px', backgroundColor: '#f5f5f5', color: 'rgba(0, 0, 0, 0.45)' }}
                      dangerouslySetInnerHTML={{ __html: emailBody || '' }}
                    />
                  </div>
                </>
              )
            }
          ]}
        />
      </Form>
    </Modal>
  )
}

export default EditConsultationModal
